import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LANG_NAMES = { es: 'Español', en: 'English', pt: 'Português' };

/** Customer e-mails: marketing switch + send any template with sample data to test it. */
const AdminCustomerEmails = () => {
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [test, setTest] = useState({ template: '05-pedido-recibido', lang: 'es', to: '' });
  const [sending, setSending] = useState(false);
  // New-order alerts to the team
  const [recipients, setRecipients] = useState('');
  const [recipientsDirty, setRecipientsDirty] = useState(false);
  const [sendingSample, setSendingSample] = useState(false);

  useEffect(() => {
    axios.get(`${API}/email/admin/settings`, { headers }).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/email/admin/templates`, { headers }).then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settings && !recipientsDirty) setRecipients((settings.admin_order_notify_emails || []).join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const saveRecipients = async () => {
    try {
      const list = recipients.split(/[\s,;]+/).filter(Boolean);
      const r = await axios.put(`${API}/email/admin/settings`, { admin_order_notify_emails: list }, { headers });
      setSettings({ ...settings, ...r.data });
      setRecipients((r.data.admin_order_notify_emails || []).join('\n'));
      setRecipientsDirty(false);
      toast({ title: 'Recipients saved', description: `${(r.data.admin_order_notify_emails || []).length} address(es)` });
    } catch (e) {
      toast({ title: 'Not saved', description: e.response?.data?.detail || 'Saving failed', variant: 'destructive' });
    }
  };

  const sendSample = async () => {
    setSendingSample(true);
    try {
      const r = await axios.post(`${API}/email/admin/order-notification-test`, {}, { headers });
      toast({ title: 'Sample sent', description: Object.keys(r.data.sent || {}).join(', ') });
    } catch (e) {
      toast({ title: 'Not sent', description: e.response?.data?.detail || 'Sending failed', variant: 'destructive' });
    } finally {
      setSendingSample(false);
    }
  };

  const save = async (patch, label) => {
    const r = await axios.put(`${API}/email/admin/settings`, patch, { headers });
    setSettings({ ...settings, ...r.data });
    toast({ title: label });
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/email/admin/test`, test, { headers });
      toast({ title: 'Test e-mail sent', description: `${test.template} (${test.lang}) → ${test.to}` });
    } catch (e) {
      toast({ title: 'Not sent', description: e.response?.data?.detail || 'Sending failed', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Card className="mt-6" data-testid="team-order-alerts">
      <CardHeader>
        <CardTitle>New-order alerts to the team</CardTitle>
        <CardDescription>
          An e-mail to the addresses below every time an order is placed (payment confirmed — unpaid checkouts do not
          count). It is sent once per order, after the shipping label step, so it says whether the label and pickup
          were created or something needs attention.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between border rounded-md p-3">
          <div>
            <Label>E-mail the team on every new order</Label>
            <p className="text-sm text-gray-500">Customer e-mails are not affected.</p>
          </div>
          <Switch data-testid="team-alerts-toggle" checked={!!settings?.admin_order_notify_enabled} disabled={!settings}
                  onCheckedChange={(on) => save({ admin_order_notify_enabled: on }, on ? 'Team alerts on' : 'Team alerts off')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="team-recipients">Recipients</Label>
          <Textarea id="team-recipients" data-testid="team-recipients" rows={4} disabled={!settings}
                    placeholder={'admin@yourcompany.com\nteam-group@yourcompany.com'}
                    value={recipients} onChange={(e) => { setRecipients(e.target.value); setRecipientsDirty(true); }} />
          <p className="text-xs text-gray-500">One per line (or comma separated), up to 20. A group address works like any other.</p>
          <div className="flex gap-3">
            <Button data-testid="team-recipients-save" onClick={saveRecipients} disabled={!settings || !recipientsDirty}
                    className="bg-[#be1f1a] hover:bg-[#a71b17]">Save recipients</Button>
            <Button data-testid="team-alert-sample" variant="outline" onClick={sendSample}
                    disabled={sendingSample || !settings?.admin_order_notify_emails?.length || recipientsDirty}>
              {sendingSample ? 'Sending…' : 'Send a sample alert'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="mt-6" data-testid="customer-emails">
      <CardHeader>
        <CardTitle>Customer e-mails</CardTitle>
        <CardDescription>
          22 templates (Spanish, English, Portuguese — by the customer's country). Account, order, shipping and support
          e-mails are always sent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between border rounded-md p-3">
          <div>
            <Label>Marketing e-mails</Label>
            <p className="text-sm text-gray-500">
              Newsletter welcome, abandoned cart (×2), review request, related products and post-purchase coupon. Only
              to customers who opted in (newsletter) and never unsubscribed. Keep off until Amazon SES approves
              marketing sending.
            </p>
          </div>
          <Switch data-testid="marketing-toggle" checked={!!settings?.marketing_enabled}
                  disabled={!settings} onCheckedChange={(on) => save({ marketing_enabled: on }, on ? 'Marketing e-mails on' : 'Marketing e-mails off')} />
        </div>

        <div className="flex items-center justify-between border rounded-md p-3">
          <div>
            <Label>Review reward (20.000 COP coupon)</Label>
            <p className="text-sm text-gray-500">
              Customers who review something they bought get a personal one-use coupon, and the review-request e-mail
              mentions it. Off: no coupon, and the reward block is hidden. Promo blocks in other e-mails (welcome
              BIENVENIDA10, cart reminder / post-purchase VUELVE15) appear only while that coupon is active in Coupons.
            </p>
          </div>
          <Switch data-testid="review-reward-toggle" checked={!!settings?.review_reward_enabled} disabled={!settings}
                  onCheckedChange={(on) => save({ review_reward_enabled: on }, on ? 'Review reward on' : 'Review reward off')} />
        </div>

        <div className="space-y-3">
          <Label>Send a test (sample data)</Label>
          <div className="grid gap-3 md:grid-cols-3">
            <Select value={test.template} onValueChange={(v) => setTest({ ...test, template: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={test.lang} onValueChange={(v) => setTest({ ...test, lang: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(LANG_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="email" placeholder="recipient@example.com" value={test.to}
                   onChange={(e) => setTest({ ...test, to: e.target.value })} />
          </div>
          <Button onClick={sendTest} disabled={sending || !test.to} className="bg-[#be1f1a] hover:bg-[#a71b17]">
            {sending ? 'Sending…' : 'Send test e-mail'}
          </Button>
          <p className="text-xs text-gray-500">While Amazon SES is in sandbox, only verified addresses receive e-mails.</p>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

export default AdminCustomerEmails;
